import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { storage, firestore, auth }  from "../../../firebase/firebase.js";
import styles from './WizardConfigAdmin.module.css';
import { useAuth } from "../../../utils/AuthContext/authcontext";
import { Navigate } from "react-router-dom";
import { useNotifications } from "../../../utils/NotificationContext/NotificationContext";
import LightNav from "./../LightNav/LightNav";
import { useTranslation } from 'react-i18next';
import Button from '../../ui/Button';

export default function WizardConfigAdmin() {
  const [config, setConfig] = useState(null);
  const { user, userData, claims } = useAuth(); 
  const { t } = useTranslation();
  const [editingStep, setEditingStep] = useState(null); // e.g. { stepId, faqs, desc }
  const { notify } = useNotifications();

  // Load current config
  useEffect(() => {
    (async () => {
      const snap = await getDoc(doc(firestore, 'wizardConfigs', 'steps'));
      if (snap.exists()) setConfig(snap.data());
    })();
  }, []);

  const save = async updated => {
    await updateDoc(doc(firestore, 'wizardConfigs', 'steps'), updated);
    setConfig(prev => ({ ...prev, ...updated }));
    setEditingStep(null);
  };

 if (!config) return <p>{t('ui.loadingConfig')}</p>;

  const steps = [1,2,3,4].map(id => ({
    id,
    desc: config?.descriptions?.[id] || [],
    faqs: config?.faqs?.[id] || []
  }));
  
  if (!user || claims?.role !== 'admin') {
    return <Navigate to="/home" />;
  }

  return (
	<>
    <LightNav/>
    <div className={styles.container}>
      <h1>{t('admin.wizardConfigTitle')}</h1>
      {steps.map(s => (
        <div key={s.id} className={styles.stepCard}>
          <h2>Step {s.id} Description</h2>
          <p>{s.desc}</p>
          <Button variant="secondary" onClick={() => setEditingStep({ ...s })}>{t('ui.edit')}</Button>
        </div>
      ))}

      {editingStep &&
        <div className={styles.editorOverlay}>
          <div className={styles.editor}>
            <h2>Edit Step {editingStep.id}</h2>

            <label>{t('admin.description')}</label>
            <textarea
              rows={3}
              value={editingStep.desc}
              onChange={e => setEditingStep(es => ({ ...es, desc: e.target.value }))}
            />

            <h3>{t('admin.faqs')}</h3>
            {editingStep.faqs.map((f, idx) => (
              <div key={idx} className={styles.faqRow}>
                <input
                  placeholder="Question"
                  value={f.question}
                  onChange={e => {
                    const faqs = [...editingStep.faqs];
                    faqs[idx].question = e.target.value;
                    setEditingStep(es => ({ ...es, faqs }));
                  }}
                />
                <textarea
                  rows={2}
                  placeholder="Answer"
                  value={f.answer}
                  onChange={e => {
                    const faqs = [...editingStep.faqs];
                    faqs[idx].answer = e.target.value;
                    setEditingStep(es => ({ ...es, faqs }));
                  }}
                />
                <Button variant="secondary" onClick={() => {
                  const faqs = editingStep.faqs.filter((_, i) => i !== idx);
                  setEditingStep(es => ({ ...es, faqs }));
                }}>{t('admin.remove')}</Button>
              </div>
            ))}

            <Button variant="secondary" onClick={() =>
              setEditingStep(es => ({ ...es, faqs: [...es.faqs, { question: '', answer: '' }] }))
            }>{t('admin.addFaq')}</Button>

            <div className={styles.editorActions}>
              <Button variant="primary" onClick={() => save({
                descriptions: { ...config?.descriptions, [editingStep.id]: editingStep.desc },
                faqs: { ...config?.faqs, [editingStep.id]: editingStep.faqs }
              })}>{t('admin.save')}</Button>
              <Button variant="secondary" onClick={() => setEditingStep(null)}>{t('admin.cancel')}</Button>
            </div>
          </div>
        </div>
      }
    </div>
	</>
  );
}
